import { RefObject } from 'react';
import { ReloadLocateScrollTarget } from '../types/context';
import { getMissingSelectedRowIds } from './getMissingSelectedRowIds';
import { getRowId } from './getRowId';

type LocateSelectionAfterReloadParams = {
  // Rows returned by the page-1 reload fetch.
  rows: unknown;
  selectedRows: any[];
  dataKey: string;
  // locateAndFetchFarthestByIds of the owning dataset context (master/slave).
  locateFarthestByIds: (
    itemIds: string[],
    onLocated?: (farthestId: string) => void
  ) => Promise<void>;
  // When set, the grid scrolls to the farthest located row once its window
  // fetch lands (reload flow). When omitted, the viewport stays where it is
  // and the loading ends right after the fetch (sorting/filter flow).
  scrollTargetRef?: RefObject<ReloadLocateScrollTarget | null>;
  setInProgress: (value: boolean) => void;
};

// Shared locate flow (reload / sorting / filter handlers, master and slave):
// when some checked rows are missing from the freshly loaded page, load
// everything up to the farthest one and optionally arm the grid scroll to it.
// setInProgress(false) fires on every exit path — early exit, failed locate,
// fetch done (no scroll), or scroll landing.
export const locateSelectionAfterReload = async ({
  rows,
  selectedRows,
  dataKey,
  locateFarthestByIds,
  scrollTargetRef,
  setInProgress,
}: LocateSelectionAfterReloadParams): Promise<void> => {
  const missingIds = Array.isArray(rows)
    ? getMissingSelectedRowIds(selectedRows, rows, dataKey)
    : [];
  if (missingIds.length === 0) {
    setInProgress(false);
    return;
  }

  const selectedIds = selectedRows
    .map((row) => getRowId(row, dataKey))
    .filter((id): id is string => id !== null);

  let scrollArmed = false;
  await locateFarthestByIds(selectedIds, (farthestId) => {
    if (!scrollTargetRef) return;
    scrollArmed = true;
    scrollTargetRef.current = {
      rowId: farthestId,
      onDone: () => setInProgress(false),
    };
  });
  if (!scrollArmed) {
    setInProgress(false);
  }
};
